<template>
  <section :class="['tb', `tb--${state}`]" aria-live="polite">
    <button class="tb__head" type="button" :aria-expanded="expanded" @click="expanded = !expanded">
      <span class="tb__lamp" aria-hidden="true"><i></i></span>
      <span class="tb__titles">
        <span class="tb__title">{{ title }}</span>
        <span class="tb__text">{{ headline }}</span>
      </span>
      <span v-if="label" class="tb__pill">{{ label }}</span>
      <span class="tb__toggle">{{ expanded ? 'Свернуть' : 'Показать' }}</span>
    </button>

    <transition name="tb-expand">
      <div v-if="expanded" class="tb__body">
        <div v-if="loading && !board" class="tb__skeleton"><span v-for="n in 4" :key="n"></span></div>
        <div v-else-if="!tiles.length" class="tb__empty">{{ error || 'Показатели пока не получены.' }}</div>
        <ul v-else class="tb__grid">
          <li
            v-for="(tile, index) in tiles"
            :key="tile.key"
            :class="['tile', `tile--${tile.state}`, { 'is-link': canOpen(tile) }]"
            :style="{ '--i': index }"
          >
            <component
              :is="canOpen(tile) ? 'button' : 'div'"
              class="tile__inner"
              :type="canOpen(tile) ? 'button' : undefined"
              :title="canOpen(tile) ? 'Показать рейсы' : ''"
              @click="canOpen(tile) && $emit('open', tile.group)"
            >
              <span class="tile__top">
                <span class="tile__dot" aria-hidden="true"></span>
                <span class="tile__label">{{ stateLabel(tile.state) }}</span>
                <span v-if="tile.rule" class="tile__q" :title="tile.rule" aria-label="Пороги цвета">?</span>
              </span>
              <span class="tile__title">{{ tile.title }}</span>
              <span v-if="tile.value_text" class="tile__value">{{ tile.value_text }}</span>
              <span v-if="tile.hint" class="tile__hint">{{ tile.hint }}</span>
              <span v-if="tile.action" class="tile__action">Что делать: {{ tile.action }}</span>
              <span v-if="canOpen(tile)" class="tile__go" aria-hidden="true">Рейсы →</span>
            </component>
          </li>
        </ul>
        <div class="tb__legend">
          <span class="lg lg--green"><i></i>норма</span>
          <span class="lg lg--yellow"><i></i>обратите внимание</span>
          <span class="lg lg--red"><i></i>срочно</span>
          <span class="lg lg--gray"><i></i>нет данных</span>
          <router-link class="tb__help" :to="{ path: '/knowledge', query: { a: 'traffic-lights' } }">Как это работает</router-link>
        </div>
      </div>
    </transition>
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { LIGHT_LABELS, boardSummary, canOpen, collapsedByDefault, sortTiles } from './lightsLogic.mjs'

const props = defineProps({
  board: { type: Object, default: null },
  // Зелёная доска сворачивается в одно окно — туда можно не смотреть.
  collapsible: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  error: { type: String, default: '' },
})
defineEmits(['open'])

const state = computed(() => (props.error && !props.board ? 'gray' : props.board?.state || 'gray'))
const title = computed(() => props.board?.title || 'Светофор')
const label = computed(() => LIGHT_LABELS[state.value] || '')
const tiles = computed(() => sortTiles(props.board?.tiles || []))
const headline = computed(() => {
  if (props.loading && !props.board) return 'Проверяем…'
  if (!props.board) return props.error ? 'Показатели недоступны — это сбой на стороне системы.' : ''
  const tail = boardSummary(props.board)
  return tail ? `${props.board.text} (${tail})` : props.board.text
})

const expanded = ref(!props.collapsible)
// Если что-то пожелтело или покраснело — доска раскрывается сама; снова всё зелёное — сворачивается.
watch(() => props.board?.state, (value) => {
  if (!props.collapsible) return
  expanded.value = !collapsedByDefault(value ? { state: value } : null)
}, { immediate: true })

function stateLabel(value) {
  return LIGHT_LABELS[value] || ''
}
</script>

<style scoped>
.tb {
  --c: #9ca3af; --soft: #f6f7f9;
  border: 1px solid #e7e5df; border-radius: 18px; background: #fff; overflow: hidden;
  box-shadow: 0 10px 30px -22px rgba(31, 36, 48, .45);
}
.tb--green { --c: #16a34a; --soft: #f0fdf4; }
.tb--yellow { --c: #d97706; --soft: #fffbeb; }
.tb--red { --c: #dc2626; --soft: #fef2f2; }
.tb--gray { --c: #9ca3af; --soft: #f6f7f9; }

.tb__head {
  display: flex; align-items: center; gap: 14px; width: 100%; padding: 14px 18px; border: 0; cursor: pointer;
  text-align: left; font: inherit; color: #1f2430; background: linear-gradient(90deg, var(--soft), #fff 70%);
}
.tb__lamp { position: relative; width: 16px; height: 16px; flex-shrink: 0; }
.tb__lamp i { position: absolute; inset: 0; border-radius: 50%; background: var(--c);
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--c) 18%, transparent); }
.tb--red .tb__lamp i, .tb--yellow .tb__lamp i { animation: tb-pulse 1.8s ease-in-out infinite; }
.tb__titles { flex: 1; display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.tb__title { font-weight: 700; font-size: 1rem; }
.tb__text { font-size: .85rem; color: #4b5563; }
.tb__pill { font-size: .75rem; font-weight: 700; color: var(--c); padding: 3px 10px; border-radius: 999px;
  background: color-mix(in srgb, var(--c) 12%, #fff); white-space: nowrap; }
.tb__toggle { font-size: .8rem; color: #8a6d00; white-space: nowrap; }

.tb__body { padding: 4px 18px 14px; }
.tb__grid { list-style: none; margin: 0; padding: 0; display: grid; gap: 10px;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); }

.tile { --c: #9ca3af; animation: tile-in .35s ease both; animation-delay: calc(var(--i) * 30ms); }
.tile--green { --c: #16a34a; }
.tile--yellow { --c: #d97706; }
.tile--red { --c: #dc2626; }
.tile--gray { --c: #9ca3af; }
.tile--neutral { --c: #6b7a99; }
.tile__inner {
  position: relative; display: flex; flex-direction: column; gap: 4px; width: 100%; height: 100%;
  padding: 12px 14px 12px; border: 1px solid #ecebe6; border-top: 4px solid var(--c); border-radius: 14px;
  background: #fff; text-align: left; font: inherit; color: inherit;
  transition: transform .15s ease, box-shadow .15s ease;
}
.tile--neutral .tile__inner { border-top-color: #e5e7eb; background: #fafaf8; }
.tile--red .tile__inner { background: #fffafa; }
.tile--yellow .tile__inner { background: #fffdf6; }
.is-link .tile__inner { cursor: pointer; }
.is-link .tile__inner:hover { transform: translateY(-2px); box-shadow: 0 12px 24px -18px rgba(31, 36, 48, .6); }
.tile__top { display: flex; align-items: center; gap: 6px; }
.tile__dot { width: 9px; height: 9px; border-radius: 50%; background: var(--c); }
.tile--neutral .tile__dot { display: none; }
.tile__label { font-size: .7rem; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: var(--c); }
.tile__q { margin-left: auto; width: 18px; height: 18px; border-radius: 50%; display: inline-grid; place-items: center;
  font-size: .7rem; color: #6b7280; background: #f3f4f6; cursor: help; }
.tile__title { font-weight: 600; font-size: .88rem; line-height: 1.25; }
.tile__value { font-size: 1.6rem; font-weight: 800; line-height: 1.1; font-variant-numeric: tabular-nums; }
.tile__hint { font-size: .78rem; color: #6b7280; line-height: 1.35;
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
.tile__action { font-size: .78rem; color: #92400e; line-height: 1.35; }
.tile__go { font-size: .75rem; color: #8a6d00; margin-top: auto; padding-top: 4px; }

.tb__legend { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 14px; padding-top: 10px;
  font-size: .72rem; color: #6b7280; }
.lg { display: inline-flex; align-items: center; gap: 5px; }
.lg i { width: 8px; height: 8px; border-radius: 50%; }
.lg--green i { background: #16a34a; } .lg--yellow i { background: #d97706; }
.lg--red i { background: #dc2626; } .lg--gray i { background: #9ca3af; }
.tb__help { margin-left: auto; color: #8a6d00; }

.tb__skeleton { display: grid; gap: 10px; grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); }
.tb__skeleton span { height: 96px; border-radius: 14px; background: linear-gradient(90deg, #f3f4f6, #fafafa, #f3f4f6);
  background-size: 200% 100%; animation: tb-shimmer 1.2s linear infinite; }
.tb__empty { font-size: .85rem; color: #6b7280; padding: 6px 0; }

.tb-expand-enter-active, .tb-expand-leave-active { transition: opacity .2s ease, transform .2s ease; }
.tb-expand-enter-from, .tb-expand-leave-to { opacity: 0; transform: translateY(-4px); }

@keyframes tb-pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.18); } }
@keyframes tile-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@keyframes tb-shimmer { to { background-position: -200% 0; } }
@media (prefers-reduced-motion: reduce) {
  .tb__lamp i, .tile, .tb__skeleton span { animation: none; }
}
@media (max-width: 640px) {
  .tb__head { padding: 12px 14px; gap: 10px; }
  .tb__toggle { display: none; }
  .tb__body { padding: 4px 12px 12px; }
  .tb__grid { grid-template-columns: 1fr 1fr; }
  .tb__help { margin-left: 0; }
}
</style>
